import { useEffect } from "react";

// Shared by every popup/modal in the app (New Appointment, Add/Edit Member, Delete confirm,
// Package flow, Profile photo picker, Notifications drawer, ...) so the page behind them can't
// be scrolled while they're open. A module-level counter (not per-hook state) means nested or
// simultaneously-open popups — e.g. Add New Member layered on top of Select Member — don't fight
// over restoring the body's styles: only the FIRST lock captures the pre-lock state, and only
// the LAST unlock restores it.
//
// `overflow: hidden` alone (the previous implementation) stops the page's own scrollbar, but
// Mobile Safari/Chrome (WKWebView) largely ignore it for touch-drag scrolling — the background
// content behind a fixed-position popup could still be dragged/rubber-banded even with
// `overflow: hidden` set. The actually-effective fix on iOS is to take the body OUT of the
// document flow entirely (`position: fixed`) for the duration of the lock, offset by the
// current scroll position so nothing visually jumps, and restore the real scroll position on
// unlock (the browser doesn't remember it once a fixed-position element has no scroll offset).
//
// Kept Mobile-only (matches this app's own `window.innerWidth >= 768` Desktop/Tablet boundary):
// on Desktop, `overflow: hidden` alone already fully works (no touch-drag scrolling to fight),
// and forcing `position: fixed` there would make the page's own scrollbar disappear for as long
// as any popup is open, shifting all content a few pixels sideways for no reason.
let lockCount = 0;
let savedBodyStyle = null;
let savedScrollY = 0;
let lockedAsMobile = false;

export default function useLockBodyScroll(active = true) {
  useEffect(() => {
    if (!active) return undefined;

    if (lockCount === 0) {
      lockedAsMobile = window.innerWidth < 768;
      savedBodyStyle = { overflow: document.body.style.overflow };
      document.body.style.overflow = "hidden";

      if (lockedAsMobile) {
        savedScrollY = window.scrollY || window.pageYOffset || 0;
        Object.assign(savedBodyStyle, {
          position: document.body.style.position,
          top: document.body.style.top,
          left: document.body.style.left,
          right: document.body.style.right,
          width: document.body.style.width,
        });
        document.body.style.position = "fixed";
        document.body.style.top = `-${savedScrollY}px`;
        document.body.style.left = "0";
        document.body.style.right = "0";
        document.body.style.width = "100%";
      }
    }
    lockCount += 1;

    return () => {
      lockCount = Math.max(0, lockCount - 1);
      if (lockCount === 0 && savedBodyStyle) {
        document.body.style.overflow = savedBodyStyle.overflow;
        if (lockedAsMobile) {
          document.body.style.position = savedBodyStyle.position;
          document.body.style.top = savedBodyStyle.top;
          document.body.style.left = savedBodyStyle.left;
          document.body.style.right = savedBodyStyle.right;
          document.body.style.width = savedBodyStyle.width;
          window.scrollTo(0, savedScrollY);
        }
        savedBodyStyle = null;
      }
    };
  }, [active]);
}
